import { Badge, Button } from '@retainerclub/common';
import { useAccount } from '@retainerclub/hooks';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import TableRowColumnEnrolled from './TableRowColumns/TableRowColumnEnrolled';
import TableRowColumnPayment from './TableRowColumns/TableRowColumnPayment';
import TableRowColumnRenewal from './TableRowColumns/TableRowColumnRenewal';

function DashboardSubscriptionsTableRow({ subscription, handleOpenModal }) {
  const { patients } = useAccount();

  const nextShipment = useMemo(() => {
    const patient = patients.find(
      (patient) => patient.id === subscription?.patient?.id,
    );

    return patient?.scheduledShipments?.find(
      (shipment) =>
        new Date(shipment.expectedShippedAt) > new Date() && !shipment.consumed,
    );
  }, [patients, subscription?.patient?.id]);

  const activeStates = ['active', 'paused'];

  // States the renewal and retry jobs still select, so money can still move. Manage has to open in
  // these: a customer sitting in a dunning state is exactly the one who needs to cancel, and until
  // now the button was disabled for them while the retry jobs kept charging the card.
  // inactive_exceeded_failures, canceled and closed are all ended states and stay blocked, as do
  // pending and awaiting_scan, which have not started and have no renewal to manage.
  const manageableStates = [
    ...activeStates,
    'inactive_no_card',
    'inactive_invalid_card',
    'inactive_failed',
  ];

  function getStatusColor(status) {
    switch (status) {
      case 'active':
        return 'green';
      case 'inactive_no_card':
      case 'inactive_invalid_card':
        return 'yellow';
      case 'inactive_failed':
      case 'inactive_exceeded_failures':
        return 'red';
      case 'cancelled':
      case 'paused':
        return 'gray';
      default:
        return 'gray';
    }
  }

  return (
    <tr>
      <td className="flex w-full flex-col justify-start gap-4 py-8 pr-3 pl-4 font-medium text-gray-900 text-sm sm:w-auto sm:max-w-none sm:flex-row sm:items-center sm:pl-0">
        <div>
          {subscription.name}
          <br />
          <span className="text-gray-500 text-xs">
            {subscription.product?.description ?? '—'}
          </span>
          <br />
          <dl className="font-normal lg:hidden">
            <dt className="sr-only">Enrolled</dt>
            <dd className="mt-1 truncate text-gray-700">
              <TableRowColumnEnrolled
                subscription={subscription}
                nextShipment={nextShipment}
                activeStates={activeStates}
              />
            </dd>
            <dt className="sr-only sm:hidden">Renewal</dt>
            <dd className="mt-1 truncate text-gray-500 sm:hidden">
              <TableRowColumnRenewal
                subscription={subscription}
                billingStates={manageableStates}
              />
            </dd>
            <dt className="sr-only sm:hidden">Payment Info</dt>
            <dd className="mt-1 truncate text-gray-500 sm:hidden">
              <TableRowColumnPayment
                subscription={subscription}
                billingStates={manageableStates}
              />
            </dd>
          </dl>
        </div>
        <div>
          <Badge
            name={subscription?.state
              ?.replace('inactive_', '')
              ?.replace('_', ' ')
              ?.replace('pending', 'upcoming')}
            withDot={false}
            size="sm"
            color={getStatusColor(subscription.state)}
          />
        </div>
      </td>
      <td className="hidden px-3 py-8 text-gray-500 text-sm lg:table-cell">
        <TableRowColumnEnrolled
          subscription={subscription}
          nextShipment={nextShipment}
          activeStates={activeStates}
        />
      </td>
      <td className="hidden px-3 py-8 text-gray-500 text-sm sm:table-cell">
        <TableRowColumnRenewal
          subscription={subscription}
          billingStates={manageableStates}
        />
      </td>
      <td className="hidden px-3 py-8 text-gray-500 text-sm sm:table-cell">
        <TableRowColumnPayment
          subscription={subscription}
          billingStates={manageableStates}
        />
      </td>
      <td className="py-8 pr-4 pl-3 text-right font-medium text-sm sm:pr-0">
        <Button
          size="sm"
          color="rcprimary"
          name="Manage"
          rounded
          disabled={!manageableStates.includes(subscription.state)}
          onClick={() => handleOpenModal(subscription)}
        />
      </td>
    </tr>
  );
}

DashboardSubscriptionsTableRow.propTypes = {
  subscription: PropTypes.object.isRequired,
  handleOpenModal: PropTypes.func.isRequired,
};

export default DashboardSubscriptionsTableRow;
